import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AbsolinDisclaimer } from "@/components/AbsolinDisclaimer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { calculateCODN, getCODNHistory, downloadCODNPDF } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  ArrowRight, ArrowLeft, Loader2, AlertTriangle, IndianRupee,
  Calculator, TrendingDown, FileText, ClipboardCheck, Package, ShieldAlert, Clock, Download, History, AlertCircle
} from "lucide-react";

const PILLAR_META = {
  manual_entry_cost: { label: "Manual Data Entry", color: "#0066CC", icon: FileText },
  reconciliation_cost: { label: "Reconciliation", color: "#059669", icon: ClipboardCheck },
  error_cost: { label: "Errors & Rework", color: "#DC2626", icon: AlertTriangle },
  reporting_cost: { label: "Reporting Delays", color: "#7C3AED", icon: Clock },
  inventory_cost: { label: "Inventory Waste", color: "#D97706", icon: Package },
  compliance_cost: { label: "Compliance Risk", color: "#EA580C", icon: ShieldAlert },
};

const PILLARS = [
  {
    key: "manual_data_entry", title: "Manual Data Entry", icon: FileText, color: "#0066CC",
    desc: "How much time does your team spend entering the same data into multiple systems?",
    fields: [
      { key: "people", label: "Number of people doing repetitive data entry", type: "number", placeholder: "4", help: "Staff entering same data in Tally + Excel + WhatsApp" },
      { key: "hours_per_day", label: "Average hours/day spent on data entry per person", type: "number", placeholder: "3", help: "Include invoice entry, stock updates, attendance" },
      { key: "avg_salary", label: "Average monthly salary of data entry staff (Rs.)", type: "number", placeholder: "25000", help: "CTC including PF, ESI contributions" },
    ]
  },
  {
    key: "reconciliation", title: "Reconciliation & Verification", icon: ClipboardCheck, color: "#059669",
    desc: "How many hours does your accounts team spend matching numbers across systems?",
    fields: [
      { key: "bank_hours", label: "Hours/month on bank reconciliation", type: "number", placeholder: "20", help: "Matching Tally entries with bank statement" },
      { key: "stock_hours", label: "Hours/month on stock reconciliation", type: "number", placeholder: "16", help: "Physical count vs system mismatch resolution" },
      { key: "ledger_hours", label: "Hours/month on vendor/customer ledger matching", type: "number", placeholder: "12", help: "Outstanding reconciliation, DN/CN matching" },
      { key: "hourly_cost", label: "Average hourly cost of accounts staff (Rs.)", type: "number", placeholder: "150", help: "Monthly salary / 208 working hours" },
    ]
  },
  {
    key: "errors", title: "Errors & Rework", icon: AlertTriangle, color: "#DC2626",
    desc: "Every billing error costs you time, reputation, and money. Let's quantify it.",
    fields: [
      { key: "billing_errors", label: "Billing/invoicing errors per month", type: "number", placeholder: "25", help: "Wrong rate, quantity, GST, duplicate invoices" },
      { key: "fix_time", label: "Average hours to fix each error", type: "number", placeholder: "1.5", help: "Including customer follow-up, credit note, re-dispatch" },
      { key: "revenue_per_error", label: "Revenue lost per pricing error (Rs.)", type: "number", placeholder: "2000", help: "Margin erosion from wrong rates" },
      { key: "pricing_errors", label: "Pricing errors per month", type: "number", placeholder: "5", help: "Wrong price list, old rates applied" },
    ]
  },
  {
    key: "reporting", title: "Reporting & Decision Delays", icon: Clock, color: "#7C3AED",
    desc: "How long does it take to get the right data to make the right decision?",
    fields: [
      { key: "mis_days", label: "Days to generate monthly MIS report", type: "number", placeholder: "12", help: "From month-end to report on MD's table" },
      { key: "people_involved", label: "People involved in MIS preparation", type: "number", placeholder: "3", help: "Collecting data from departments, compiling" },
      { key: "delayed_decisions", label: "Decisions delayed per month due to lack of data", type: "number", placeholder: "4", help: "Pricing, purchase, production planning decisions" },
      { key: "decision_value", label: "Average value of each delayed decision (Rs.)", type: "number", placeholder: "15000", help: "Lost order, excess purchase, production idle time" },
    ]
  },
  {
    key: "inventory", title: "Inventory Waste", icon: Package, color: "#D97706",
    desc: "Dead stock, excess inventory, and stockouts are the silent profit killers.",
    fields: [
      { key: "total_value", label: "Total inventory value (Rs.)", type: "number", placeholder: "5000000", help: "Current stock value across all warehouses" },
      { key: "dead_stock_pct", label: "Estimated dead/obsolete stock (%)", type: "number", placeholder: "15", help: "Items not moved in 6+ months" },
      { key: "excess_pct", label: "Estimated excess stock above optimal (%)", type: "number", placeholder: "20", help: "Over-ordered items, safety stock overkill" },
      { key: "stockouts", label: "Stockout incidents per month", type: "number", placeholder: "8", help: "Orders lost because item was out of stock" },
      { key: "lost_order_value", label: "Average value per lost order (Rs.)", type: "number", placeholder: "25000", help: "Revenue from orders you couldn't fulfil" },
    ]
  },
  {
    key: "compliance", title: "Compliance & Penalty Risk", icon: ShieldAlert, color: "#EA580C",
    desc: "Late filings and errors aren't just penalties — they're audit triggers.",
    fields: [
      { key: "gst_delays", label: "GST return filing delays per year", type: "number", placeholder: "3", help: "Late GSTR-1, GSTR-3B filings" },
      { key: "late_fee", label: "Average late fee per delayed filing (Rs.)", type: "number", placeholder: "100", help: "Rs.50/day CGST + Rs.50/day SGST" },
      { key: "tds_errors", label: "TDS/TCS filing errors per year", type: "number", placeholder: "2", help: "Wrong deductions, missed deductions" },
      { key: "audit_observations", label: "Statutory audit observations last year", type: "number", placeholder: "4", help: "System/process gap related observations" },
    ]
  }
];

export default function CODNPage() {
  const [step, setStep] = useState("landing");
  const [currentPillar, setCurrentPillar] = useState(0);
  const [inputs, setInputs] = useState({});
  const [contactInfo, setContactInfo] = useState({ company_name: "", industry: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async (email) => {
    if (!email) return;
    setHistoryLoading(true);
    try { const res = await getCODNHistory(email); setHistory(res.data.calculations || []); }
    catch { /* no history */ }
    finally { setHistoryLoading(false); }
  };

  const downloadReport = async (reportData) => {
    const r = reportData || result;
    if (!r) return;
    try {
      toast.info("Generating PDF report...");
      const res = await downloadCODNPDF(r.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CODN_${r.company_name.replace(/\s+/g, '_')}_${new Date(r.created_at).toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const maxReached = history.length >= 3;

  const updateField = (pillarKey, fieldKey, value) => {
    setInputs(prev => ({
      ...prev,
      [pillarKey]: { ...(prev[pillarKey] || {}), [fieldKey]: parseFloat(value) || 0 }
    }));
  };

  const handleSubmit = async () => {
    if (!contactInfo.company_name || !contactInfo.contact_name || !contactInfo.contact_email) {
      toast.error("Please fill in company name, your name, and email");
      return;
    }
    setLoading(true);
    try {
      const res = await calculateCODN({ ...contactInfo, inputs });
      setResult(res.data);
      setStep("results");
      loadHistory(contactInfo.contact_email);
    } catch { toast.error("Failed to calculate"); }
    finally { setLoading(false); }
  };

  // LANDING
  if (step === "landing") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 flex-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-50 border border-red-100 rounded-full text-xs text-red-700 font-semibold uppercase tracking-wider mb-6">
                <TrendingDown className="w-3.5 h-3.5" /> Financial Reality Check
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" data-testid="codn-title">
                Cost of
                <span className="text-red-600"> Doing Nothing</span>
              </h1>
              <p className="text-slate-500 text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
                Your business bleeds money every day through manual processes, errors, and delays. This calculator puts a number on it. The truth might surprise you.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {PILLARS.slice(0, 6).map((p, i) => (
                <Card key={i} className="border border-slate-200 hover:border-red-200 transition-colors">
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-lg mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${p.color}10` }}>
                      <p.icon className="w-5 h-5" style={{ color: p.color }} />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-xs">{p.title}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button
                size="lg"
                onClick={() => setStep("calculator")}
                className="bg-red-600 hover:bg-red-700 text-white px-8 h-12 text-sm font-semibold rounded-xl"
                data-testid="start-codn-btn"
              >
                Calculate Your Hidden Costs <Calculator className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-slate-400 text-xs mt-3">Takes 3-5 minutes. 6 cost areas to evaluate. Maximum 3 attempts.</p>
            </div>
          </div>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  // CALCULATOR FORM
  if (step === "calculator") {
    const pillar = PILLARS[currentPillar];
    const isLast = currentPillar === PILLARS.length - 1;
    const Icon = pillar.icon;

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-4">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-slate-500 font-medium">Pillar {currentPillar + 1} of {PILLARS.length}</span>
              <span className="text-xs font-bold" style={{ color: pillar.color }}>{Math.round(((currentPillar + 1) / PILLARS.length) * 100)}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((currentPillar + 1) / PILLARS.length) * 100}%`, backgroundColor: pillar.color }} />
            </div>
          </div>

          {/* Pillar tabs */}
          <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
            {PILLARS.map((p, i) => (
              <button key={p.key} onClick={() => setCurrentPillar(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  i === currentPillar ? "text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                style={i === currentPillar ? { backgroundColor: p.color } : {}}>
                {p.title.split(" ")[0]}
              </button>
            ))}
          </div>

          {/* Pillar Card */}
          <Card className="border-t-4" style={{ borderTopColor: pillar.color }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon className="w-6 h-6" style={{ color: pillar.color }} />
                <h2 className="text-lg font-bold text-slate-900">{pillar.title}</h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">{pillar.desc}</p>

              <div className="space-y-4">
                {pillar.fields.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{f.label}</Label>
                    <Input
                      type="number"
                      placeholder={f.placeholder}
                      value={inputs[pillar.key]?.[f.key] || ""}
                      onChange={e => updateField(pillar.key, f.key, e.target.value)}
                      className="mt-1 placeholder:text-slate-300"
                      data-testid={`codn-${pillar.key}-${f.key}`}
                    />
                    <p className="text-xs text-slate-400 mt-1">{f.help}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <Button variant="outline" disabled={currentPillar === 0}
              onClick={() => setCurrentPillar(p => p - 1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            {isLast ? (
              <Button onClick={() => setStep("contact")} className="bg-red-600 hover:bg-red-700">
                Review & Calculate <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={() => setCurrentPillar(p => p + 1)} style={{ backgroundColor: pillar.color }}>
                Next Pillar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CONTACT + SUBMIT
  if (step === "contact") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 max-w-lg mx-auto px-4">
          <button onClick={() => setStep("calculator")} className="text-sm text-slate-500 hover:text-red-600 flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to calculator
          </button>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Almost there!</h2>
          <p className="text-slate-500 text-sm mb-6">Enter your details to see your Cost of Doing Nothing report.</p>

          <Card>
            <CardContent className="p-6 space-y-4">
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Company Name *</Label>
                <Input className="placeholder:text-slate-300" value={contactInfo.company_name} onChange={e => setContactInfo(p => ({...p, company_name: e.target.value}))} placeholder="e.g. ABC Manufacturing Pvt Ltd" data-testid="codn-company" /></div>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Industry</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={contactInfo.industry} onChange={e => setContactInfo(p => ({...p, industry: e.target.value}))}>
                  <option value="">Select Industry</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail & Distribution</option>
                  <option value="FMCG">FMCG / Consumer Goods</option>
                  <option value="Textile">Textile & Apparel</option>
                  <option value="Pharma">Pharma & Healthcare</option>
                  <option value="Agro">Agro-Processing & Food</option>
                  <option value="Other">Other</option>
                </select></div>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your Name *</Label>
                <Input className="placeholder:text-slate-300" value={contactInfo.contact_name} onChange={e => setContactInfo(p => ({...p, contact_name: e.target.value}))} placeholder="e.g. Rajesh Kumar" data-testid="codn-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email *</Label>
                  <Input className="placeholder:text-slate-300" type="email" value={contactInfo.contact_email}
                    onChange={e => setContactInfo(p => ({...p, contact_email: e.target.value}))}
                    onBlur={e => loadHistory(e.target.value)}
                    placeholder="e.g. you@company.com" data-testid="codn-email" /></div>
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</Label>
                  <Input className="placeholder:text-slate-300" value={contactInfo.contact_phone} onChange={e => setContactInfo(p => ({...p, contact_phone: e.target.value}))} placeholder="e.g. +91 98765 43210" /></div>
              </div>

              {historyLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking previous calculations...</div>}
              {history.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
                    <History className="w-4 h-4" /> Previous Calculations ({history.length}/3)
                  </div>
                  {history.map((h, i) => (
                    <div key={h.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-100">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{h.company_name} — Rs.{(h.total_codn || 0).toLocaleString('en-IN')}/yr</p>
                        <p className="text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => downloadReport(h)}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    </div>
                  ))}
                  {maxReached && (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                      <AlertCircle className="w-4 h-4" /> Maximum 3 calculations reached. Download your reports above.
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full bg-red-600 hover:bg-red-700 h-11" disabled={loading || maxReached} onClick={handleSubmit} data-testid="codn-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
                {maxReached ? "Maximum Attempts Reached" : "Calculate My Hidden Costs"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // RESULTS
  if (step === "results" && result) {
    const chartData = Object.entries(result.pillar_costs).map(([key, value]) => ({
      name: PILLAR_META[key]?.label || key,
      value,
      color: PILLAR_META[key]?.color || "#0066CC"
    })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);

    const formatRupee = (v) => {
      if (v >= 10000000) return `${(v/10000000).toFixed(1)} Cr`;
      if (v >= 100000) return `${(v/100000).toFixed(1)} L`;
      if (v >= 1000) return `${(v/1000).toFixed(0)}K`;
      return v.toFixed(0);
    };

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 flex-1">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900" data-testid="codn-results-title">Your Cost of Doing Nothing</h1>
            <p className="text-slate-500 mt-1">{result.company_name}</p>
          </div>

          {/* Big Number */}
          <Card className="mb-8 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <IndianRupee className="w-10 h-10 text-red-600" />
                <span className="text-5xl sm:text-6xl font-extrabold text-red-600" data-testid="codn-total">
                  {result.total_codn >= 100000 ? `${(result.total_codn / 100000).toFixed(1)}L` : result.total_codn.toLocaleString("en-IN")}
                </span>
              </div>
              <p className="text-slate-500 text-sm">Annual hidden cost your business is bleeding</p>
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-200 rounded-full">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-slate-800">
                  That's Rs.{result.daily_loss?.toLocaleString("en-IN")} every single day
                </span>
              </div>
              <div className="mt-4">
                <Button variant="outline" onClick={() => downloadReport()} data-testid="download-codn-report">
                  <Download className="w-4 h-4 mr-2" /> Download Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart */}
          {chartData.length > 0 && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Cost Breakdown by Category</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                      <XAxis type="number" tickFormatter={v => `Rs.${formatRupee(v)}`} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip formatter={v => [`Rs.${v.toLocaleString("en-IN")}`, "Annual Cost"]} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                        {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pillar Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {Object.entries(result.pillar_costs).map(([key, value]) => {
              const meta = PILLAR_META[key];
              if (!meta || value === 0) return null;
              const Icon = meta.icon;
              return (
                <Card key={key} className="border border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5" style={{ color: meta.color }} />
                      <span className="text-xs font-semibold text-slate-600">{meta.label}</span>
                    </div>
                    <div className="text-2xl font-bold" style={{ color: meta.color }}>
                      Rs.{value.toLocaleString("en-IN")}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">per year</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Impact Statements */}
          {result.impact_statements?.length > 0 && (
            <Card className="mb-8 border border-amber-200 bg-amber-50/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" /> What These Numbers Mean
                </h3>
                <div className="space-y-3">
                  {result.impact_statements.map((s, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-white rounded-lg border border-amber-100">
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-amber-700">{i + 1}</span>
                      </div>
                      <p className="text-sm text-slate-700">{s}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Stop the bleeding. Start the transformation.</h3>
              <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto">
                Most businesses recover their ERP investment within 12-18 months. Let's show you how.
              </p>
              <Button className="bg-[#0066CC] hover:bg-[#0055aa] px-8 h-11" data-testid="codn-book-call-btn"
                onClick={() => window.open("https://calendly.com", "_blank")}>
                Talk to an ERP Consultant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  return null;
}
